import { PlaneTypes } from '@Components/BigBrowse/Model/ModelTypes';
import { MultiSectionDescription } from '@Components/BigBrowse/bb2/meshes2/texts/MultiSectionDescription';
import { observer } from 'mobx-react-lite';

import { PLANE_DEFAULT_Z } from '../../BigBrowseConfig';
import type { GridCard } from '../../Model/Classes/GridCard';
import { BigBrowseBookend } from './cards/BigBrowseBookend';
import { BigBrowseProduct } from './cards/BigBrowseProduct';
import { MultiSectionFooterItem } from './cards/MultiSectionFooterItem';
import { MultiSectionFooterItemSmall } from './cards/MultiSectionFooterItemSmall';
import { MultiSectionHero } from './cards/MultiSectionHero';
import { MultiSectionProduct } from './cards/MultiSectionProduct';
import { MultiSectionSectionHero } from './cards/MultiSectionSectionHero';
import { MultiSectionSectionHeroSmall } from './cards/MultiSectionSectionHeroSmall';
import SectionBackground from './cards/SectionBackground';
import { ShopHeroCard } from './cards/ShopHeroCard';
import { ShopProductCard } from './cards/ShopProductCard';
import { HeroAndTextImagePart } from './heroAndText/HeroAndTextImagePart';
import { HeroAndTextTextPart } from './heroAndText/HeroAndTextTextPart';
import { Merchandiser } from './merchandiser/Merchandiser';
import { MultiSectionDescriptionBody } from './texts/MultiSectionDescriptionBody';
import { MultiSectionDescriptionTitle } from './texts/MultiSectionDescriptionTitle';
import { ProductSubtitle } from './texts/ProductSubtitle';
import { SingleSectionDescription } from './texts/SingleSectionDescription';
import { ThreeUp } from './threeUp/ThreeUp';

export type GridCardMeshProps<T extends PlaneTypes> = {
  item: GridCard<T>;
  onClick?: () => void;
};

export const GridCardMesh2 = observer(function GridCardMesh2({
  item,
  onClick,
}: GridCardMeshProps<PlaneTypes>) {
  if (item.type === PlaneTypes.Card_ShopFloor_Product) {
    return (
      <ShopProductCard
        item={item as GridCard<PlaneTypes.Card_ShopFloor_Product>}
        onClick={onClick}
      />
    );
  }

  if (item.type === PlaneTypes.Card_BigBrowse_Product) {
    return (
      <BigBrowseProduct
        item={item as GridCard<PlaneTypes.Card_BigBrowse_Product>}
        onClick={onClick}
      />
    );
  }

  if (
    item.type === PlaneTypes.Card_MultiSection_Product ||
    item.type === PlaneTypes.Card_SingleSectionView_Product
  ) {
    return (
      <MultiSectionProduct
        item={
          item as GridCard<
            | PlaneTypes.Card_MultiSection_Product
            | PlaneTypes.Card_SingleSectionView_Product
          >
        }
        onClick={onClick}
      />
    );
  }

  if (item.type === PlaneTypes.Card_ShopFloor_Hero) {
    return (
      <ShopHeroCard
        item={item as GridCard<PlaneTypes.Card_ShopFloor_Hero>}
        onClick={onClick}
      />
    );
  }

  if (item.type === PlaneTypes.Card_BigBrowse_Bookend) {
    return (
      <BigBrowseBookend
        item={item as GridCard<PlaneTypes.Card_BigBrowse_Bookend>}
        onClick={onClick}
      />
    );
  }

  if (
    item.type === PlaneTypes.Card_MultiSection_Hero ||
    item.type === PlaneTypes.Card_SingleSectionView_Hero
  ) {
    return (
      <MultiSectionHero
        item={item as GridCard<PlaneTypes.Card_MultiSection_Hero>}
      />
    );
  }

  if (item.type === PlaneTypes.Card_MultiSection_SectionHero) {
    return (
      <MultiSectionSectionHero
        item={item as GridCard<PlaneTypes.Card_MultiSection_SectionHero>}
      />
    );
  }

  if (item.type === PlaneTypes.Card_MultiSection_SectionHero_Small) {
    return (
      <MultiSectionSectionHeroSmall
        item={item as GridCard<PlaneTypes.Card_MultiSection_SectionHero_Small>}
      />
    );
  }

  if (item.type === PlaneTypes.Card_MultiSection_Footer_Item) {
    return (
      <MultiSectionFooterItem
        item={item as GridCard<PlaneTypes.Card_MultiSection_Footer_Item>}
        onClick={onClick}
      />
    );
  }

  if (item.type === PlaneTypes.Card_MultiSection_Footer_Item_Small) {
    return (
      <MultiSectionFooterItemSmall
        item={item as GridCard<PlaneTypes.Card_MultiSection_Footer_Item_Small>}
        onClick={onClick}
      />
    );
  }

  if (
    item.type === PlaneTypes.Card_SingleSectionView_Subtitle ||
    item.type === PlaneTypes.Card_MultiSection_Subtitle ||
    item.type === PlaneTypes.Card_MultiSection_Subtitle_Ultra_Small
  ) {
    return (
      <ProductSubtitle
        item={item as GridCard<PlaneTypes.Card_MultiSection_Subtitle>}
      />
    );
  }

  if (item.type === PlaneTypes.Card_SingleSectionView_Description) {
    return (
      <SingleSectionDescription
        item={item as GridCard<PlaneTypes.Card_SingleSectionView_Description>}
      />
    );
  }

  if (
    item.type === PlaneTypes.Card_MultiSection_Description_Title ||
    item.type === PlaneTypes.Card_MultiSection_Description_Title_Small ||
    item.type === PlaneTypes.Card_MultiSection_Description_Title_Ultra_Small ||
    item.type === PlaneTypes.Card_MultiSection_SectionHero_Small_Title
  ) {
    return (
      <MultiSectionDescriptionTitle
        item={
          item as GridCard<
            | PlaneTypes.Card_MultiSection_Description_Title
            | PlaneTypes.Card_MultiSection_Description_Title_Small
            | PlaneTypes.Card_MultiSection_Description_Title_Ultra_Small
            | PlaneTypes.Card_MultiSection_SectionHero_Small_Title
          >
        }
      />
    );
  }

  if (item.type === PlaneTypes.Card_MultiSection_Description) {
    return (
      <MultiSectionDescription
        item={item as GridCard<PlaneTypes.Card_MultiSection_Description>}
      />
    );
  }

  if (
    item.type === PlaneTypes.Card_MultiSection_Description_Small ||
    item.type === PlaneTypes.Card_MultiSection_Description_Body_Small ||
    item.type === PlaneTypes.Card_MultiSection_Description_Body_Ultra_Small ||
    item.type === PlaneTypes.Card_MultiSection_SectionHero_Small_Description
  ) {
    return (
      <MultiSectionDescriptionBody
        item={
          item as GridCard<
            | PlaneTypes.Card_MultiSection_Description
            | PlaneTypes.Card_MultiSection_Description_Body_Small
            | PlaneTypes.Card_MultiSection_Description_Body_Ultra_Small
            | PlaneTypes.Card_MultiSection_SectionHero_Small_Description
          >
        }
      />
    );
  }

  if (item.type === PlaneTypes.Layout_Merchandiser) {
    return (
      <Merchandiser
        gridCard={item as GridCard<PlaneTypes.Layout_Merchandiser>}
      />
    );
  }

  if (item.type === PlaneTypes.Layout_ThreeUp) {
    return <ThreeUp gridCard={item as GridCard<PlaneTypes.Layout_ThreeUp>} />;
  }

  if (item.type === PlaneTypes.Layout_HeroAndText_ImagePart) {
    return (
      <HeroAndTextImagePart
        gridCard={item as GridCard<PlaneTypes.Layout_HeroAndText_ImagePart>}
      />
    );
  }

  if (item.type === PlaneTypes.Layout_HeroAndText_TextPart) {
    return (
      <HeroAndTextTextPart
        gridCard={item as GridCard<PlaneTypes.Layout_HeroAndText_TextPart>}
      />
    );
  }

  if (item.type === PlaneTypes.Section_Background) {
    return (
      <SectionBackground
        gridCard={item as GridCard<PlaneTypes.Section_Background>}
      />
    );
  }

  if (item.type === PlaneTypes.Header_Background) {
    return (
      <SectionBackground
        gridCard={item as GridCard<PlaneTypes.Header_Background>}
      />
    );
  }

  // fallback: typically this will be a GAP component if gaps are enabled to display
  return (
    <mesh
      position={[item.worldPosition.x, item.worldPosition.y, PLANE_DEFAULT_Z]}
    >
      <planeBufferGeometry
        attach='geometry'
        args={[item.size.width, item.size.height]}
      />
      <meshBasicMaterial color={item.backgroundColor} />
    </mesh>
  );
});
